import { ReactElement } from 'react'
import { useFieldArray } from 'react-hook-form'
import Add from '@mui/icons-material/Add'
import Delete from '@mui/icons-material/Delete'
import { Button, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { TextField, useFieldArrayErrors } from '@ui/forms'
import type { ProjectFieldFormValues } from '../schema'
import { MAX_OPTION_LABEL_LENGTH } from '../utils'

/**
 * The options of a select field: the label is what the project form's dropdown shows, the value
 * (possibly several lines, e.g. an address block) is what the documentation templates print.
 */
const ProjectFieldOptionList = (): ReactElement => {
  const { fields, append, remove } = useFieldArray<ProjectFieldFormValues, 'options'>({
    name: 'options',
  })
  const error = useFieldArrayErrors('options')

  return (
    <Stack spacing={1} data-cy="project-field-options">
      <Typography variant="subtitle2">Optionen</Typography>
      {fields.map((field, index) => (
        <Stack
          key={field.id}
          direction="row"
          spacing={1}
          alignItems="flex-start"
          data-cy={`project-field-option-${index}`}
        >
          <TextField
            name={`options.${index}.label`}
            label={index === 0 ? 'Bezeichnung (im Auswahlmenü)' : undefined}
            placeholder={`max. ${MAX_OPTION_LABEL_LENGTH} Zeichen`}
            sx={{ flex: 1 }}
            data-cy={`project-field-option-label-${index}`}
          />
          <TextField
            name={`options.${index}.value`}
            label={index === 0 ? 'Wert (im Dokument)' : undefined}
            multiline
            rows={3}
            placeholder="Kann mehrzeilig sein, z. B. Name und Anschrift"
            sx={{ flex: 2 }}
            data-cy={`project-field-option-value-${index}`}
          />
          <Tooltip title="Option entfernen">
            <IconButton
              size="small"
              onClick={() => remove(index)}
              sx={{ mt: index === 0 ? 3 : 0.5 }}
              data-cy={`project-field-option-delete-${index}`}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ))}
      {error?.message && (
        <Typography variant="caption" color="error">
          {error.message}
        </Typography>
      )}
      <Button
        size="small"
        startIcon={<Add />}
        onClick={() => append({ id: null, label: '', value: '' })}
        sx={{ alignSelf: 'flex-start' }}
        data-cy="project-field-option-add"
      >
        Option hinzufügen
      </Button>
    </Stack>
  )
}

export default ProjectFieldOptionList
